import type { Goal, GoalProgressInfo, GoalStatus, Deal } from "../types";

/**
 * Utilitários para formatação e cálculo de metas comerciais
 */

export const formatCurrency = (val: number): string => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  }).format(val);
};

export const formatGoalValue = (
  val: number,
  metricType: "volume" | "value",
): string => {
  if (metricType === "value") {
    return formatCurrency(val);
  }
  return `${Math.round(val)} ${Math.round(val) === 1 ? "negócio" : "negócios"}`;
};

/**
 * Calcula os dias restantes até o fim da meta
 */
export const getDaysRemaining = (endDateStr: string): number => {
  const end = new Date(endDateStr);
  end.setHours(23, 59, 59, 999);
  const now = new Date();
  const diffTime = end.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Calcula o progresso de uma meta a partir dos negócios carregados
 */
export const calculateGoalProgress = (
  goal: Goal,
  deals: Deal[],
  salesName?: string,
): GoalProgressInfo => {
  const daysLeft = getDaysRemaining(goal.end_date);
  const isEnded = daysLeft < 0;

  const startDate = new Date(goal.start_date);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(goal.end_date);
  endDate.setHours(23, 59, 59, 999);

  // Filtra negócios pertencentes à etapa, período e (se aplicável) vendedor
  const matchingDeals = deals.filter((deal: any) => {
    // 1. Etapa
    const dealStage = deal.stage || deal.status;
    if (dealStage !== goal.stage) return false;

    // 2. Funil (se especificado)
    if (
      goal.pipeline_id &&
      deal.pipeline_id &&
      deal.pipeline_id !== goal.pipeline_id
    ) {
      return false;
    }

    // 3. Vendedor (se for meta individual)
    if (goal.scope === "usuario" && goal.sales_id) {
      const dealSalesId = deal.sales_id || deal.vendedor_id;
      if (Number(dealSalesId) !== Number(goal.sales_id)) return false;
    }

    // 4. Data do negócio (considera data de criação ou última movimentação)
    const dealDateStr = deal.updated_at || deal.created_at;
    if (!dealDateStr) return true;
    const dealDate = new Date(dealDateStr);
    return dealDate >= startDate && dealDate <= endDate;
  });

  // Calcula realizado
  let achieved = 0;
  if (goal.metric_type === "value") {
    achieved = matchingDeals.reduce((sum, d: any) => {
      const val = Number(d.amount ?? d.valor ?? 0);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
  } else {
    achieved = matchingDeals.length;
  }

  const target = Number(goal.target_value) || 1;
  const percent = Math.round((achieved / target) * 100);
  const remaining = Math.max(0, target - achieved);

  // Ritmo de tempo vs realizado
  const totalDays = Math.max(
    1,
    Math.ceil(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
    ),
  );
  const elapsedDays = Math.min(
    totalDays,
    Math.max(
      0,
      Math.ceil((Date.now() - startDate.getTime()) / (1000 * 60 * 60 * 24)),
    ),
  );
  const elapsedRatio = elapsedDays / totalDays;

  let status: GoalStatus = "on_track";
  if (percent >= 100) {
    status = "beat";
  } else if (isEnded) {
    status = "ended";
  } else {
    const expectedPercent = elapsedRatio * 100;
    if (percent >= expectedPercent * 0.9) {
      status = "on_track";
    } else if (percent >= expectedPercent * 0.6) {
      status = "warning";
    } else {
      status = "late";
    }
  }

  return {
    goal,
    achieved,
    remaining,
    percent,
    daysRemaining: Math.max(0, daysLeft),
    status,
    isEnded,
    salesPersonName: salesName,
  };
};

/**
 * Retorna estilos e cores de acordo com o status da meta
 */
export const getGoalStatusBadge = (status: GoalStatus, percent: number) => {
  switch (status) {
    case "beat":
      return {
        label: "Meta Batida",
        bgClass:
          "bg-emerald-500/10 text-emerald-600 border-emerald-500/30 dark:text-emerald-400",
        barClass: "bg-emerald-500",
        ringClass: "ring-emerald-500",
        icon: "🌟",
      };
    case "on_track":
      return {
        label: `${percent}% • No Ritmo`,
        bgClass:
          "bg-blue-500/10 text-blue-600 border-blue-500/30 dark:text-blue-400",
        barClass: "bg-blue-500",
        ringClass: "ring-blue-500",
        icon: "🟢",
      };
    case "warning":
      return {
        label: `${percent}% • Atenção`,
        bgClass:
          "bg-amber-500/10 text-amber-600 border-amber-500/30 dark:text-amber-400",
        barClass: "bg-amber-500",
        ringClass: "ring-amber-500",
        icon: "🟡",
      };
    case "late":
      return {
        label: `${percent}% • Atrasado`,
        bgClass:
          "bg-rose-500/10 text-rose-600 border-rose-500/30 dark:text-rose-400",
        barClass: "bg-rose-500",
        ringClass: "ring-rose-500",
        icon: "🔴",
      };
    case "ended":
      return {
        label: percent >= 100 ? "Concluída com Sucesso" : "Encerrada",
        bgClass: "bg-muted text-muted-foreground border-border",
        barClass: percent >= 100 ? "bg-emerald-500" : "bg-muted-foreground",
        ringClass: "ring-muted-foreground",
        icon: "🏁",
      };
  }
};
